import { useState } from "react";
import clsx from "clsx";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CHIP_TONE } from "@/pages/PortfolioBuilder/components/builder/chipTone";

const COLLAPSED_LIMIT = 14;

interface TermChipsProps {
  tickers?: string[];
  keywords?: string[];
  /** Tag names. */
  tags?: string[];
  /** Omit to render read-only chips (library lists can't be edited here). */
  onRemoveTicker?: (ticker: string) => void;
  onRemoveKeyword?: (keyword: string) => void;
}

/**
 * The contents of a watchlist, as chips in the builder's colours: blue for
 * a ticker, violet for a keyword, green for a tag. Long lists collapse to a
 * readable first row with a "+N more" toggle — a 444-ticker index list
 * should not bury the rest of the plan.
 */
export default function TermChips({
  tickers = [],
  keywords = [],
  tags = [],
  onRemoveTicker,
  onRemoveKeyword,
}: TermChipsProps) {
  const [expanded, setExpanded] = useState(false);
  const entries = [
    ...tickers.map((label) => ({ label, kind: "ticker" as const, remove: onRemoveTicker })),
    ...keywords.map((label) => ({ label, kind: "keyword" as const, remove: onRemoveKeyword })),
    ...tags.map((label) => ({ label, kind: "tag" as const, remove: undefined })),
  ];
  const overflowing = entries.length > COLLAPSED_LIMIT;
  const shown = overflowing && !expanded ? entries.slice(0, COLLAPSED_LIMIT) : entries;
  const hidden = entries.length - shown.length;

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {shown.map(({ label, kind, remove }, i) => (
        <span
          key={`${kind}-${label}-${i}`}
          className={clsx(
            "inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded border text-xs",
            remove ? "pl-1.5 pr-0.5" : "px-1.5",
            kind === "ticker" && "font-semibold",
            CHIP_TONE[kind]
          )}
        >
          {label}
          {remove && (
            <button
              type="button"
              onClick={() => remove(label)}
              aria-label={`Remove ${label}`}
              className="inline-flex size-4 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
            >
              <XMarkIcon className="size-3" strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </span>
      ))}
      {overflowing && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="h-[22px] cursor-pointer rounded border-none bg-transparent px-1.5 py-0 text-xs font-medium text-gray-400 transition-colors hover:text-squawk-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70"
        >
          {expanded ? "Show less" : `+${hidden.toLocaleString()} more`}
        </button>
      )}
    </div>
  );
}
